import React, { useState, useEffect } from 'react';
import Header from '../components/landing/Header';
import TopBar from '../components/landing/TopBar';
import Footer from '../components/landing/Footer';
import EnquiryModal from '../components/landing/EnquiryModal';
import { useLocation } from 'react-router-dom';
import { Calendar, ArrowRight } from 'lucide-react';
import api from '../utils/axios';
import getFullUrl from '../utils/formatUrl';

const Blog = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isInquiryOpen, setIsInquiryOpen] = useState(false);
    const [content, setContent] = useState({});
    const [blogPosts, setBlogPosts] = useState([]);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        
        fetchContent();
        window.scrollTo(0, 0);

        return () => window.removeEventListener('scroll', handleScroll);
    }, [location]);

    const fetchContent = async () => {
        try {
            const res = await api.get('/content');
            const contentMap = {};
            res.data.data.forEach(item => {
                contentMap[item.key] = item.value;
            });
            setContent(contentMap);
            
            // Fetch Dynamic Blogs
            const blogRes = await api.get('/blogs/active');
            setBlogPosts(blogRes.data.data);
        } catch (err) {
            console.error("Failed to load web content", err);
        }
    };

    return (
        <div className="min-h-screen bg-white font-sans text-slate-900">
            {!isScrolled && <TopBar data={content} />}
            <Header 
                isScrolled={isScrolled} 
                onApply={() => setIsInquiryOpen(true)}
                logo={content.site_logo}
                email={content.contact_email}
                phone={content.contact_phone}
                applyUrl={content.apply_url}
                colleges={content.colleges_list}
            />

            {/* Palit-Inspired Internal Hero */}
            <header className="relative h-[450px] bg-slate-900 flex items-center overflow-hidden">
                <div className="absolute inset-0 bg-black/60 z-10 transition-opacity duration-1000"></div>
                <img 
                    src="https://images.unsplash.com/photo-1497215728101-856f4ea42174?q=80&w=2000&auto=format&fit=crop" 
                    className="absolute inset-0 w-full h-full object-cover scale-105"
                    alt="Blog Hero"
                />
                <div className="max-w-7xl mx-auto px-4 relative z-20 w-full text-center">
                    <h4 className="text-white font-black uppercase tracking-[6px] text-xs mb-3 animate-in fade-in slide-in-from-bottom-2 duration-700">Blogs insight Palit</h4>
                    <h1 className="text-6xl md:text-8xl font-black text-white uppercase tracking-tighter drop-shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-1000">
                        Our <span className="text-[#ee3124]">Blogs</span>
                    </h1>
                    <div className="h-1.5 w-32 bg-[#ee3124] mx-auto mt-8"></div>
                </div>
            </header>

            {/* Blog Grid - Palit Styled */}
            <section className="py-24 bg-[#f8f9fa]">
                <div className="max-w-7xl mx-auto px-4">
                    {blogPosts.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                            {blogPosts.map((post, idx) => (
                                <a 
                                    href={`/blog-details?name=${post.slug}`}
                                    key={idx} 
                                    className="group cursor-pointer flex flex-col h-full bg-[#212529] rounded-[10px] overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-500"
                                >
                                    <div className="relative aspect-video bg-[#ee3124] overflow-hidden p-2">
                                        <img 
                                            src={getFullUrl(post.image)} 
                                            className="w-full h-full object-cover rounded-sm group-hover:scale-105 transition-transform duration-700 opacity-90 group-hover:opacity-100" 
                                            alt={post.title} 
                                        />
                                    </div>
                                    
                                    <div className="p-8 flex-1 flex flex-col items-center">
                                        <span className="bg-slate-700/50 text-white text-[9px] font-black uppercase py-1 px-4 rounded-full tracking-widest mb-3 border border-white/10">
                                            Published
                                        </span>
                                        <div className="flex items-center text-[10px] font-bold text-slate-400 tracking-widest uppercase mb-6">
                                            <Calendar size={12} className="mr-2 text-[#ee3124]" />
                                            {post.date}
                                        </div>
                                        
                                        <h3 className="text-xl font-black text-white text-center uppercase tracking-tight group-hover:text-[#ee3124] transition-colors leading-tight mb-4">
                                            {post.title}
                                        </h3>



                                        <div className="mt-auto flex justify-center">
                                            <div className="w-10 h-10 rounded-full border border-slate-700 flex items-center justify-center group-hover:bg-[#ee3124] group-hover:border-[#ee3124] transition-all duration-300">
                                                <ArrowRight size={18} className="text-white" />
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-32 bg-white rounded-xl shadow-sm border border-slate-100">
                             <div className="bg-[#ee3124]/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                                <ArrowRight size={32} className="text-[#ee3124] rotate-[-45deg]" />
                             </div>
                             <h2 className="text-2xl font-black uppercase text-slate-900 tracking-tighter italic">No Stories Published Yet</h2>
                             <p className="text-slate-400 text-sm uppercase font-bold tracking-widest mt-2">Check back later for campus insights</p>
                        </div>
                    )}
                </div>
            </section>

            <Footer data={content} />
            <EnquiryModal isOpen={isInquiryOpen} onClose={() => setIsInquiryOpen(false)} />
        </div>
    );
};

export default Blog;
